import { supabase } from "@/integrations/supabase/client";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";

export interface SecuritySettings {
  id: string;
  userId: string;
  two_factor_enabled: boolean;
  last_password_change: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface PasswordChangeForm {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

/**
 * Fetch security settings for a user
 */
export const fetchSecuritySettings = async (userId: string): Promise<SecuritySettings | null> => {
  try {
    const { data, error } = await supabase
      .from('security_settings')
      .select('*')
      .eq('user_id', userId)
      .single();
      
    if (error) {
      console.error("Error fetching security settings:", error);
      return null;
    }
    
    return {
      id: data.id,
      userId: data.user_id,
      two_factor_enabled: data.two_factor_enabled,
      last_password_change: data.last_password_change ? new Date(data.last_password_change) : null,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at)
    };
  } catch (error) {
    console.error("Error fetching security settings:", error);
    return null;
  }
};

/**
 * Update two-factor authentication setting for a user
 */
export const updateSecurityTwoFactor = async (userId: string, enabled: boolean): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('security_settings')
      .update({ two_factor_enabled: enabled })
      .eq('user_id', userId);
      
    if (error) {
      console.error("Error updating two-factor authentication:", error);
      toast.error(`Failed to update two-factor authentication: ${error.message}`);
      return false;
    }
    
    toast.success('Two-factor authentication setting updated successfully');
    return true;
  } catch (error) {
    console.error("Error updating two-factor authentication:", error);
    toast.error(`Failed to update two-factor authentication: ${error instanceof Error ? error.message : 'Unknown error'}`);
    return false;
  }
};

/**
 * Update a user's password
 */
export const updatePassword = async (userId: string, passwordForm: PasswordChangeForm): Promise<boolean> => {
  try {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error('Please fill in all password fields.');
      return false;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match.');
      return false;
    }
    
    const { data, error } = await supabase.auth.updateUser({
      password: passwordForm.newPassword,
    });
    
    if (error) {
      console.error("Error updating password:", error);
      toast.error(`Failed to update password: ${error.message}`);
      return false;
    }
    
    toast.success('Password updated successfully');
    return true;
  } catch (error) {
    console.error("Error updating password:", error);
    toast.error(`Failed to update password: ${error instanceof Error ? error.message : 'Unknown error'}`);
    return false;
  }
};

/**
 * Delete a user's account completely
 * This will remove the user from auth and cascade delete all their data
 */
export const deleteUserAccount = async (userId: string): Promise<boolean> => {
  try {
    // Get the current user's token
    const token = useAuthStore.getState().token;
    
    if (!token) {
      console.error('No auth token available');
      toast.error('Authentication error. Please try logging in again.');
      return false;
    }

    // Call the delete-user edge function with proper authorization
    const { error } = await supabase.functions.invoke('delete-user', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (error) {
      console.error('Error deleting user account:', error);
      toast.error(`Failed to delete account: ${error.message || 'Unknown error'}`);
      return false;
    }

    toast.success('Your account has been deleted successfully');
    return true;
  } catch (error) {
    console.error('Exception deleting user account:', error);
    toast.error(`Failed to delete account: ${error instanceof Error ? error.message : 'Unknown error'}`);
    return false;
  }
};
